import React, { useCallback, useEffect, useState } from "react";
import { MoreHorizontal, Edit2, Trash2, Flag, Check, X } from "lucide-react";
import { toast } from "sonner";
import { DossierLabel } from "../dossier/DossierLabel";
import { useAuth } from "../../context/AuthContext";
import {
  fetchTopicComments,
  createTopicComment,
  editComment,
  deleteComment,
  reportComment,
} from "../../lib/api";

const MAX = 1500;

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
};

const CommentRow = ({ c, onChange, viewerId }) => {
  const { token } = useAuth();
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body);
  const [busy, setBusy] = useState(false);
  const removed = c.status === "removed";
  const mine = c.is_mine || (viewerId && c.user_id === viewerId);

  const doEdit = async () => {
    const body = (draft || "").trim();
    if (body.length < 1 || body.length > MAX) {
      toast.error(`Comment must be 1-${MAX} characters.`);
      return;
    }
    setBusy(true);
    try {
      await editComment(c.id, body, token);
      toast.success("Comment updated.");
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Edit failed.");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete your comment?")) return;
    setBusy(true);
    try {
      await deleteComment(c.id, token);
      toast.success("Comment deleted.");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally { setBusy(false); setMenu(false); }
  };

  const doReport = async () => {
    setBusy(true);
    try {
      await reportComment(c.id, null, token);
      toast.success("Reported. Thanks — moderators will review.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Report failed.";
      toast.message(detail);
    } finally { setBusy(false); setMenu(false); }
  };

  return (
    <li
      data-testid={`comment-${c.id}`}
      className="flex gap-3 border-b border-[#1F1C18] py-4 first:pt-2"
    >
      <div className="shrink-0">
        {c.user_picture ? (
          <img
            src={c.user_picture}
            alt=""
            className="w-8 h-8 grayscale opacity-80"
          />
        ) : (
          <div className="w-8 h-8 bg-[#2D2A26] flex items-center justify-center font-mono text-xs text-[#A39E93]">
            {(c.user_name || "?").charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-baseline gap-2 flex-wrap min-w-0">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E4DFD3]">
              {removed ? "[removed]" : c.user_name}
            </span>
            <span className="font-mono text-[10px] text-[#757065]">· {timeAgo(c.created_at)}</span>
            {c.edited_at && !removed && (
              <span className="font-mono text-[10px] text-[#757065] italic">(edited)</span>
            )}
          </div>
          {!removed && (mine || token) && (
            <div className="relative shrink-0">
              <button
                onClick={() => setMenu((m) => !m)}
                data-testid={`comment-menu-${c.id}`}
                className="text-[#757065] hover:text-[#E4DFD3] p-1"
                aria-label="Comment actions"
              >
                <MoreHorizontal size={16} />
              </button>
              {menu && (
                <div className="absolute right-0 top-7 z-30 bg-[#0F0F0F] border border-[#2D2A26] min-w-[140px] py-1">
                  {mine ? (
                    <>
                      <button
                        onClick={() => { setEditing(true); setMenu(false); }}
                        disabled={busy}
                        data-testid={`comment-edit-${c.id}`}
                        className="w-full text-left px-3 py-2 flex items-center gap-2 text-xs text-[#E4DFD3] hover:bg-[#1a1a1a]"
                      >
                        <Edit2 size={12} /> Edit
                      </button>
                      <button
                        onClick={doDelete} disabled={busy}
                        data-testid={`comment-delete-${c.id}`}
                        className="w-full text-left px-3 py-2 flex items-center gap-2 text-xs text-[#D92D20] hover:bg-[#1a1a1a]"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={doReport} disabled={busy}
                      data-testid={`comment-report-${c.id}`}
                      className="w-full text-left px-3 py-2 flex items-center gap-2 text-xs text-[#CBB892] hover:bg-[#1a1a1a]"
                    >
                      <Flag size={12} /> Report
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {removed ? (
          <div className="text-[13px] text-[#757065] italic">
            [comment removed]
          </div>
        ) : editing ? (
          <div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX))}
              data-testid={`comment-edit-input-${c.id}`}
              rows={3}
              className="w-full bg-[#0F0F0F] border border-[#2D2A26] text-[#E4DFD3] p-2 font-body text-[14px] focus:outline-none focus:border-[#D92D20]"
            />
            <div className="flex items-center justify-between mt-1">
              <span className="font-mono text-[10px] text-[#757065]">{draft.length}/{MAX}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => { setEditing(false); setDraft(c.body); }}
                  className="border border-[#454038] text-[#A39E93] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-1"
                >
                  <X size={11} /> Cancel
                </button>
                <button
                  onClick={doEdit} disabled={busy}
                  data-testid={`comment-edit-save-${c.id}`}
                  className="border border-[#D92D20] text-[#D92D20] hover:bg-[#1a0808] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-1 disabled:opacity-50"
                >
                  <Check size={11} /> Save
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-[15px] text-[#E4DFD3] leading-relaxed whitespace-pre-wrap break-words">
            {c.body}
          </div>
        )}
      </div>
    </li>
  );
};

export const CommentsSection = ({ slug }) => {
  const { user, token, login, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const r = await fetchTopicComments(slug, 50, token);
      setItems(r.comments || []);
      setTotal(r.total_visible || 0);
    } catch { /* keep last state */ }
    finally { setLoading(false); }
  }, [slug, token]);

  useEffect(() => { load(); }, [load]);

  const post = useCallback(async () => {
    const body = (draft || "").trim();
    if (body.length < 1) { toast.error("Write something first."); return; }
    if (body.length > MAX) { toast.error(`Max ${MAX} characters.`); return; }
    setPosting(true);
    try {
      await createTopicComment(slug, body, token);
      setDraft("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post comment.");
    } finally { setPosting(false); }
  }, [slug, draft, token, load]);

  return (
    <section className="mt-14" data-testid="comments-section">
      <DossierLabel as="h2">Live Discussion</DossierLabel>
      <h3 className="font-heading text-3xl text-[#E4DFD3] mb-2">
        {total} {total === 1 ? "Perspective" : "Perspectives"}
      </h3>
      <p className="text-sm text-[#A39E93] mb-6">
        Add to the record. Be specific. Cite where you can.
      </p>

      {/* Composer */}
      {!authLoading && (
        user ? (
          <div className="mb-6">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX))}
              placeholder="Add your perspective…"
              rows={4}
              data-testid="comment-input"
              className="w-full bg-[#0F0F0F] border border-[#2D2A26] text-[#E4DFD3] p-3 font-body text-[15px] focus:outline-none focus:border-[#D92D20]"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="font-mono text-[10px] text-[#757065]">
                {draft.length}/{MAX} · posting as <span className="text-[#E4DFD3]">{user.name}</span>
              </span>
              <button
                onClick={post}
                disabled={posting || draft.trim().length === 0}
                data-testid="comment-submit"
                className="border border-[#D92D20] bg-[#1a0808] text-[#D92D20] hover:bg-[#D92D20] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
              >
                {posting ? "Posting…" : "// Post Comment"}
              </button>
            </div>
          </div>
        ) : (
          <div className="mb-6 border border-[#2D2A26] bg-[#131313] p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] mb-2">
              // sign in to add your perspective
            </div>
            <button
              onClick={() => {
                try { sessionStorage.setItem("archive_post_login_path",
                  window.location.pathname + window.location.search); } catch { /* noop */ }
                login();
              }}
              data-testid="comments-signin-btn"
              className="border border-[#D92D20] bg-[#1a0808] text-[#D92D20] hover:bg-[#D92D20] hover:text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              Sign in with Google
            </button>
          </div>
        )
      )}

      {/* List */}
      {loading && items.length === 0 ? (
        <div className="text-[#757065] italic text-sm">Loading discussion…</div>
      ) : items.length === 0 ? (
        <div data-testid="comments-empty" className="border border-dashed border-[#2D2A26] text-[#757065] py-8 text-center font-mono text-xs uppercase tracking-[0.2em]">
          No perspectives yet. Be the first to add to the record.
        </div>
      ) : (
        <ul className="divide-y divide-[#1F1C18]" data-testid="comments-list">
          {items.map((c) => (
            <CommentRow key={c.id} c={c} onChange={load} viewerId={user?.user_id} />
          ))}
        </ul>
      )}
    </section>
  );
};

export default CommentsSection;
